import {useEffect, useMemo, useState} from 'react'
import {useLocation, useHistory} from 'react-router-dom'
import {useQueries, useQueryClient} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import fetch from 'cross-fetch'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getMrtApiBasepath} from '../ssr/helpers'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useBooleanCustomPreference} from '../hooks/use-custom-preference'

export const usePlpProducts = (parameters) => {
    const location = useLocation()
    const history = useHistory()
    const {site, locale} = useMultiSite()
    const origin = useAppOrigin()
    const {getTokenWhenReady} = useAccessToken()
    const currentPage = new URLSearchParams(location.search).get('page') || 1
    const [pagesToFetch, setPagesToFetch] = useState([parameters.searchParams.page || 1])
    const {currency} = useCurrency()
    const queryClient = useQueryClient()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    // when we navigate to page 1 we need to reset the pagesToFetch state
    // happens when we add filters and the page param is reset
    useEffect(() => {
        if (currentPage === 1) {
            setPagesToFetch([1])
        }
    }, [currentPage])

    // we can't include the page from the searchParam in the query keys
    //  since it's not the actual page we are fetching in some cases.
    const searchParamsCopy = {...parameters.searchParams}
    delete searchParamsCopy.page

    const generateQueryKey = (page) => [
        'plp-products',
        parameters.urlSegment,
        page,
        JSON.stringify(searchParamsCopy),
        locale.id,
    ]

    const query = async (page) => {
        const token = await getTokenWhenReady()
        const endpoint = origin + getMrtApiBasepath('/product-search')

        const modifiedParameters = {...parameters}
        if (
            modifiedParameters.searchParams?.q &&
            !modifiedParameters.searchParams.q.endsWith('*')
        ) {
            modifiedParameters.searchParams = {
                ...modifiedParameters.searchParams,
                q: `${modifiedParameters.searchParams.q}*`,
            }
        }

        const options = {
            headers: {
                authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            method: 'POST',
            credentials: 'same-origin',
            body: JSON.stringify({
                ...modifiedParameters,
                locale: locale.id,
                site: site.id,
                currency,
                page,
                notShoppableCountry,
            }),
        }
        try {
            const response = await fetch(`${endpoint}`, options)
            const data = await response.json()

            return {
                ...data,
                page, // Return the page variable
            }
        } catch (e) {
            console.error(e)
            return e
        }
    }

    // to optimize the useQueries later if there are a lot of products
    // https://github.com/TanStack/query/issues/6840
    // current performance for 20 products: 0.01586 ms
    const pageQueries = useQueries({
        queries: pagesToFetch.map((page) => {
            return {
                queryKey: generateQueryKey(page),
                queryFn: () => query(page),
                staleTime: 5 * 60 * 1000, // 5 minutes
            }
        }),
    })

    const error = useMemo(() => {
        return pageQueries.find((query) => query.error)?.error || null
    }, [pageQueries])

    const isError = useMemo(() => {
        return pageQueries.every((query) => !!query?.data?.error)
    }, [pageQueries])

    const products = useMemo(() => {
        return pageQueries.flatMap((query) => query?.data?.productSearchResult?.hits || []) || []
    }, [pageQueries])

    const lastPage = pageQueries[pageQueries.length - 1]?.data?.productSearchResult
    const hasNextPage = useMemo(() => {
        return lastPage && lastPage.offset + lastPage.limit < lastPage.total
    }, [pageQueries])

    const hasPreviousPage = useMemo(() => {
        return Math.min(...pagesToFetch) > 1
    }, [pagesToFetch])

    const fetchNextPage = () => {
        const nextPage = Math.max(...pagesToFetch) + 1
        setPagesToFetch((prev) => [...prev, nextPage])

        const searchParams = new URLSearchParams(location.search)
        searchParams.set('page', nextPage)
        history.replace(`${location.pathname}?${searchParams.toString()}`)
    }

    const fetchPreviousPage = () => {
        const previousPage = Math.min(...pagesToFetch) - 1
        if (previousPage > 0) {
            setPagesToFetch((prev) => [previousPage, ...prev])
        }

        const searchParams = new URLSearchParams(location.search)
        if (previousPage === 1) {
            history.replace(`${location.pathname}`)
        } else {
            searchParams.set('page', previousPage)
            history.replace(`${location.pathname}?${searchParams.toString()}`)
        }
    }

    const prefetchNextPage = async () => {
        const nextPage = Math.max(...pagesToFetch) + 1

        await queryClient.prefetchQuery({
            queryKey: generateQueryKey(nextPage),
            queryFn: () => query(nextPage),
            staleTime: 5 * 60 * 1000,
        })
    }

    const prefetchPreviousPage = async () => {
        const previousPage = Math.min(...pagesToFetch) - 1

        if (previousPage < 1) return

        await queryClient.prefetchQuery({
            queryKey: generateQueryKey(previousPage),
            queryFn: () => query(previousPage),
            staleTime: 5 * 60 * 1000,
        })
    }

    const isLoading = useMemo(() => {
        return pageQueries.every((query) => query.isLoading)
    }, [pageQueries])

    const nonEmptyPage = pageQueries
        .slice()
        .reverse()
        .find((query) => query.isLoading === false)

    const isFirstPageFetched = pagesToFetch.includes(1)

    return {
        productSearchResult: {
            ...nonEmptyPage?.data?.productSearchResult,
            hits: products,
            page: nonEmptyPage?.data?.page, // Return the page variable
        },
        products,
        productsCount: lastPage ? lastPage.offset + lastPage.limit : 0,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage: pageQueries[pageQueries.length - 1]?.isLoading,
        hasPreviousPage,
        fetchPreviousPage,
        isFetchingPreviousPage: pageQueries[0]?.isLoading,
        previousPage: Math.min(...pagesToFetch) - 1, // Add previousPage
        isError,
        error,
        isLoading,
        prefetchNextPage,
        prefetchPreviousPage,
        isFirstPageFetched,
    }
}
